import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, ShoppingCart, RotateCcw, Trash2, Share2, BookmarkPlus, ChevronRight, X, Copy, Plus, Pencil } from 'lucide-react';
import { useAppStore, MyPickItem } from '@/hooks/useAppStore';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ROUTE_PATHS, SavedTrip } from '@/lib/index';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';

function sp(v: unknown): number {
  if (typeof v === 'number') return isNaN(v) ? 0 : Math.max(0, v);
  const p = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
  return isNaN(p) ? 0 : Math.max(0, p);
}

function sectionFor(name: string): string {
  const n = name.toLowerCase();
  if (['chicken','beef','pork','lamb','turkey','duck','steak','rib','brisket','roast','ground','mince','sausage','bacon','ham','tenderloin','loin'].some(k=>n.includes(k))) return '🥩 Meat & Poultry';
  if (['salmon','shrimp','fish','cod','tuna','halibut','trout','scallop','crab','prawn'].some(k=>n.includes(k))) return '🐟 Fish & Seafood';
  if (['broccoli','carrot','celery','onion','potato','spinach','kale','lettuce','cucumber','zucchini','mushroom','corn','asparagus','cauliflower','cabbage','squash','leek','bok choy','rapini','pepper','tomato','avocado','apple','pear','lemon','lime','orange','mango','berry','grape'].some(k=>n.includes(k))) return '🥦 Produce';
  if (['milk','cheese','butter','cream','yogurt','egg','cheddar','mozzarella','parmesan','feta'].some(k=>n.includes(k))) return '🧀 Dairy & Eggs';
  if (['pasta','spaghetti','penne','rice','noodle','flour','bread','pita','tortilla'].some(k=>n.includes(k))) return '🌾 Dry Goods & Grains';
  if (['olive oil','vegetable oil','canola oil','soy sauce','vinegar','mustard','honey','maple','hot sauce','worcestershire','fish sauce','sesame'].some(k=>n.includes(k))) return '🍯 Condiments & Oils';
  if (['garlic powder','onion powder','paprika','cumin','turmeric','oregano','thyme','rosemary','cinnamon','salt','pepper','chili','cayenne','coriander','spice'].some(k=>n.includes(k))) return '🧂 Spices & Herbs';
  if (['broth','stock','coconut milk','tomato paste','crushed tomato','canned','bean','chickpea','lentil'].some(k=>n.includes(k))) return '🥫 Canned & Pantry';
  return '🛒 Other';
}

const SECTION_ORDER = ['🥩 Meat & Poultry','🐟 Fish & Seafood','🥦 Produce','🧀 Dairy & Eggs','🌾 Dry Goods & Grains','🥫 Canned & Pantry','🍯 Condiments & Oils','🧂 Spices & Herbs','🛒 Other'];

export default function GroceryListPage() {
  const { cartMeals, mealPlan, selectedStore, savedTrips, removeFromCart, saveTrip, flyerItems, clearFlyerItems, removeFlyerItem, myPickItems, addMyPick, removeMyPick, clearMyPicks } = useAppStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checked,    setChecked]   = useState<Set<string>>(new Set());
  const [picksChecked, setPicksChecked] = useState<Set<string>>(new Set());
  const [newPickText,  setNewPickText]  = useState('');
  const [showPickInput, setShowPickInput] = useState(false);
  const [showSave,  setShowSave]  = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [tripName,  setTripName]  = useState('');
  const [savedMsg,  setSavedMsg]  = useState(false);
  const [copyMsg,   setCopyMsg]   = useState(false);

  if (!mealPlan && !cartMeals.length) { navigate(ROUTE_PATHS.HOME); return null; }

  const storeName = selectedStore?.name || mealPlan?.store_name || 'Store';

  const toggle = (key: string) => setChecked(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  // Build sections from cartMeals ingredients
  const sections = useMemo(() => {
    const map = new Map<string, { name:string; qty:string; price:number; used_in:string; key:string }[]>();
    cartMeals.forEach(meal => {
      (meal.ingredients ?? []).forEach((ing, i) => {
        const sec = sectionFor(ing.name ?? '');
        if (!map.has(sec)) map.set(sec, []);
        map.get(sec)!.push({
          name: ing.name, qty: ing.qty, price: sp(ing.price),
          used_in: meal.name, key: `${meal.id}-${i}`,
        });
      });
    });
    return SECTION_ORDER.filter(s => map.has(s)).map(s => ({ section: s, items: map.get(s)! }));
  }, [cartMeals]);

  const totalCost = useMemo(() =>
    cartMeals.reduce((s, m) => s + (m.ingredients?.reduce((ss, i) => ss + sp(i.price), 0) ?? 0), 0),
    [cartMeals]
  );

  const flyerTotal = flyerItems.reduce((s, i) => s + (i.price ?? 0), 0);
  const combinedTotal = totalCost + flyerTotal;
  const allKeys   = sections.flatMap(s => s.items.map(i => i.key));
  const doneCount = allKeys.filter(k => checked.has(k)).length + myPickItems.filter(p => picksChecked.has(p.id)).length;
  const totalItems = allKeys.length + myPickItems.length;
  const pct = totalItems ? Math.round((doneCount / totalItems) * 100) : 0;

  // Build plain text for sharing
  const buildText = () => {
    const date = new Date().toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
    const lines: string[] = [
      `🛒 FlyersFeast Grocery List`,
      `${storeName} · ${date}`,
      `${cartMeals.length} meals · $${totalCost.toFixed(2)} est. total`,
      '',
    ];
    sections.forEach(sec => {
      lines.push(sec.section);
      sec.items.forEach(item => {
        const price = item.price > 0 ? ` — $${item.price.toFixed(2)}` : '';
        lines.push(`  ☐ ${item.name}  ${item.qty}${price}`);
      });
      lines.push('');
    });
    lines.push(`Meals: ${cartMeals.map(m => m.name).join(', ')}`);
    return lines.join('\n');
  };

  // Share: try native share sheet first, fall back to showing text modal
  const handleShare = async () => {
    const text = buildText();
    // 1. Try native share (mobile: opens WhatsApp / Messages / Notes etc.)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'FlyersFeast Grocery List', text });
        return; // success — done
      } catch { /* user cancelled or not supported */ }
    }
    // 2. Try clipboard API
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopyMsg(true);
        setTimeout(() => setCopyMsg(false), 2500);
        return;
      } catch { /* clipboard blocked */ }
    }
    // 3. Last resort: show the text in a modal so user can copy it manually
    setShowShare(true);
  };

  const handleCopyFromModal = () => {
    const text = buildText();
    // textarea trick — works on all browsers including Android WebView
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(el);
    el.focus();
    el.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(el);
    setCopyMsg(true);
    setTimeout(() => setCopyMsg(false), 2500);
  };

  // Save current list to Saved Lists
  const handleSave = () => {
    const name = tripName.trim() ||
      `${storeName} — ${new Date().toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`;
    const trip: SavedTrip = {
      id:        crypto.randomUUID(),
      name,
      store:     storeName,
      savedAt:   new Date().toISOString(),
      totalCost,
      meals:     cartMeals.map(m => m.name),
      sections:  sections.map(sec => ({
        section: sec.section,
        items:   sec.items.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
      })),
    };
    saveTrip(trip);
    // Sync to Supabase if logged in
    if (user) {
      supabase.from('user_saved_trips').upsert({
        id:         trip.id,
        user_id:    user.id,
        name:       trip.name,
        store_name: trip.store,
        meals:      { names: trip.meals, sections: trip.sections },
        total_cost: trip.totalCost,
        created_at: trip.savedAt,
      }, { onConflict: 'id' })
      .then(({ error }) => { if (error) console.error('[FF] save trip:', error.message); });
    }
    setShowSave(false);
    setTripName('');
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">

      {/* ── Header ── */}
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-2">
          <button onClick={() => navigate(ROUTE_PATHS.MEAL_PLAN)} className="text-muted-foreground hover:text-foreground shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="font-bold text-foreground">Grocery List</span>
            <span className="text-xs text-muted-foreground ml-2">
              {storeName} · {cartMeals.length} meal{cartMeals.length !== 1 ? 's' : ''}
            </span>
          </div>
          {/* Share */}
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 text-xs font-semibold bg-muted hover:bg-muted/80 text-foreground px-3 py-1.5 rounded-full transition-colors shrink-0"
          >
            <Share2 className="w-3.5 h-3.5" />
            {copyMsg ? 'Copied!' : 'Share'}
          </button>
          {/* Save */}
          <button
            onClick={() => setShowSave(true)}
            className="flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity shrink-0"
          >
            <BookmarkPlus className="w-3.5 h-3.5" />
            {savedMsg ? 'Saved!' : 'Save'}
          </button>
        </div>
      </header>

      {/* ── Share text modal (fallback when native share + clipboard both fail) ── */}
      <AnimatePresence>
        {showShare && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            onClick={(e) => { if (e.target === e.currentTarget) setShowShare(false); }}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              transition={springPresets.snappy}
              className="bg-background rounded-t-2xl w-full max-w-xl p-5 pb-10"
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-foreground">Your Grocery List</h2>
                <button onClick={() => setShowShare(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Long-press the text below and select <strong>Select All → Copy</strong>, then paste into Notes or WhatsApp.
              </p>
              <textarea
                readOnly
                className="w-full h-48 text-xs font-mono bg-muted border border-border rounded-xl p-3 text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                value={buildText()}
                onFocus={e => e.target.select()}
              />
              <button
                onClick={handleCopyFromModal}
                className="w-full mt-3 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {copyMsg ? '✓ Copied to clipboard!' : 'Copy to Clipboard'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Save dialog overlay ── */}
      <AnimatePresence>
        {showSave && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            onClick={(e) => { if (e.target === e.currentTarget) setShowSave(false); }}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              transition={springPresets.snappy}
              className="bg-background rounded-t-2xl w-full max-w-xl p-6 pb-10"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">Save Shopping List</h2>
                <button onClick={() => setShowSave(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Give this list a name so you can find it later in <strong>Saved Lists</strong>.
              </p>
              <input
                type="text"
                value={tripName}
                onChange={e => setTripName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                placeholder={`${storeName} — ${new Date().toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`}
                className="w-full border border-border rounded-xl px-4 py-3 text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSave(false)}
                  className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-opacity"
                >
                  Save List
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container mx-auto max-w-xl px-4 py-5">

        {/* View saved lists link */}
        {savedTrips.length > 0 && (
          <button
            onClick={() => navigate(ROUTE_PATHS.SAVED_LISTS)}
            className="w-full flex items-center justify-between bg-primary/10 hover:bg-primary/15 text-primary rounded-xl px-4 py-3 mb-4 transition-colors"
          >
            <span className="text-sm font-semibold">
              📋 You have {savedTrips.length} saved list{savedTrips.length !== 1 ? 's' : ''}
            </span>
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Progress + totals */}
        <motion.div
          {...{ initial:{opacity:0,y:12}, animate:{opacity:1,y:0}, transition:springPresets.gentle }}
          className="bg-card border border-border rounded-2xl p-4 mb-5"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Shopping Progress</span>
            </div>
            <span className="text-sm font-bold text-primary">{pct}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div className="h-full bg-primary rounded-full"
              initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={springPresets.gentle} />
          </div>
          <div className="flex justify-between mt-3 text-xs text-muted-foreground">
            <span>Est. total: <strong className="text-foreground">${combinedTotal.toFixed(2)}</strong>{flyerItems.length > 0 && <span className="text-primary ml-1">({flyerItems.length} flyer picks)</span>}</span>
            <span>{cartMeals.length} meal{cartMeals.length !== 1 ? 's' : ''} · {cartMeals.reduce((s,m) => s+(sp(m.serves)||4),0)} servings</span>
          </div>
        </motion.div>

        {/* Meal summary chips */}
        <div className="flex flex-wrap gap-2 mb-5">
          {cartMeals.map(m => (
            <div key={m.id} className="flex items-center gap-1.5 text-[10px] font-semibold bg-primary/10 text-primary rounded-full pl-2.5 pr-1 py-1">
              {m.name}
              <button onClick={() => removeFromCart(m.id)}
                className="hover:text-red-500 transition-colors rounded-full hover:bg-red-100 p-0.5">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>

        {sections.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            <p>No meals in your list yet.</p>
            <button onClick={() => navigate(ROUTE_PATHS.MEAL_PLAN)}
              className="mt-2 text-primary font-semibold hover:underline">← Back to meal plan</button>
          </div>
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-4">
            {sections.map(section => (
              <motion.div key={section.section} variants={staggerItem}
                className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 border-b border-border/50">
                  <span className="text-base">{section.section.split(' ')[0]}</span>
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {section.section.replace(/^[^\s]+\s/, '')}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">{section.items.length} items</span>
                </div>
                {section.items.map(item => {
                  const done = checked.has(item.key);
                  return (
                    <button key={item.key} onClick={() => toggle(item.key)}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border/40 last:border-0 text-left transition-colors ${done ? 'bg-muted/20' : 'hover:bg-muted/20'}`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${done ? 'bg-primary border-primary' : 'border-border'}`}>
                        {done && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {item.name}
                        </span>
                        <div className="text-[10px] text-muted-foreground truncate">{item.used_in}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        {item.qty && <div className="text-xs text-muted-foreground">{item.qty}</div>}
                        <div className={`text-sm font-bold tabular-nums ${done ? 'text-muted-foreground' : 'text-foreground'}`}>
                          {item.price > 0 ? `$${item.price.toFixed(2)}` : <span className="text-xs text-muted-foreground font-normal">pantry</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="mt-8 space-y-4">
          {/* ── Flyer Picks section ── */}
          {flyerItems.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between px-3 py-2 bg-primary/10 rounded-t-xl border border-primary/20">
              <span className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                🏷️ Flyer Picks
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{flyerItems.length} items · ${flyerTotal.toFixed(2)}</span>
                <button onClick={clearFlyerItems} className="text-[10px] text-muted-foreground hover:text-destructive transition-colors">Clear all</button>
              </div>
            </div>
            <div className="border border-t-0 border-primary/20 rounded-b-xl overflow-hidden">
              {flyerItems.map((item) => (
                <div key={item.name} className="flex items-center justify-between px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors">
                  <div className="min-w-0 flex-1 pr-2">
                    <span className="text-sm font-medium text-foreground leading-tight line-clamp-1">{item.name}</span>
                    <span className="text-[10px] text-muted-foreground capitalize">{item.cat}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-bold text-foreground tabular-nums">${(item.price ?? 0).toFixed(2)}</span>
                    <button onClick={() => removeFlyerItem(item.name)}
                      className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-red-50 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── My Picks section ── */}
        <div className="mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-violet-500/10 rounded-t-xl border border-violet-500/20">
            <span className="text-xs font-bold text-violet-400 uppercase tracking-wider flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> My Picks
            </span>
            <div className="flex items-center gap-3">
              {myPickItems.length > 0 && (
                <span className="text-xs text-muted-foreground">{myPickItems.length} item{myPickItems.length !== 1 ? 's' : ''}</span>
              )}
              {myPickItems.length > 0 && (
                <button onClick={clearMyPicks} className="text-[10px] text-muted-foreground hover:text-destructive transition-colors">Clear all</button>
              )}
              <button
                onClick={() => setShowPickInput(v => !v)}
                className="flex items-center gap-1 text-[10px] font-bold text-violet-400 hover:text-violet-300 transition-colors">
                <Plus className="w-3 h-3" /> Add item
              </button>
            </div>
          </div>

          <div className="border border-t-0 border-violet-500/20 rounded-b-xl overflow-hidden">
            {/* Add item input */}
            <AnimatePresence>
              {showPickInput && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-violet-500/15 bg-violet-500/5">
                    <input
                      type="text"
                      autoFocus
                      placeholder="Add item (e.g. Milk, Bread, Eggs…)"
                      value={newPickText}
                      onChange={e => setNewPickText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newPickText.trim()) {
                          addMyPick(newPickText.trim());
                          setNewPickText('');
                        }
                        if (e.key === 'Escape') { setShowPickInput(false); setNewPickText(''); }
                      }}
                      className="flex-1 h-9 px-3 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                    />
                    <button
                      disabled={!newPickText.trim()}
                      onClick={() => {
                        if (newPickText.trim()) {
                          addMyPick(newPickText.trim());
                          setNewPickText('');
                        }
                      }}
                      className="h-9 px-3 rounded-xl bg-violet-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-violet-600 transition-colors shrink-0">
                      Add
                    </button>
                    <button onClick={() => { setShowPickInput(false); setNewPickText(''); }}
                      className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Items */}
            {myPickItems.length === 0 && !showPickInput ? (
              <div className="px-4 py-4 text-center">
                <p className="text-xs text-muted-foreground">Add anything extra you need — milk, snacks, household items, etc.</p>
                <button onClick={() => setShowPickInput(true)}
                  className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-violet-400 hover:text-violet-300 transition-colors mx-auto">
                  <Plus className="w-3.5 h-3.5" /> Add your first item
                </button>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {myPickItems.map(pick => {
                  const done = picksChecked.has(pick.id);
                  return (
                    <motion.div
                      key={pick.id}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                      className={`flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 transition-colors ${done ? 'bg-muted/20' : 'hover:bg-muted/10'}`}>
                      {/* Checkbox */}
                      <button
                        onClick={() => setPicksChecked(prev => {
                          const next = new Set(prev);
                          next.has(pick.id) ? next.delete(pick.id) : next.add(pick.id);
                          return next;
                        })}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                          done ? 'bg-violet-500 border-violet-500 text-white' : 'border-border hover:border-violet-400'
                        }`}>
                        {done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                      </button>
                      {/* Name */}
                      <span className={`flex-1 text-sm font-medium transition-all ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        {pick.name}
                      </span>
                      {/* Remove */}
                      <button onClick={() => removeMyPick(pick.id)}
                        className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-red-50 transition-colors shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button onClick={() => { setChecked(new Set()); setPicksChecked(new Set()); }}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Uncheck all
          </button>
          <button
            onClick={() => navigate(ROUTE_PATHS.SAVED_LISTS)}
            className="flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline">
            View Saved Lists <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>{/* end mt-4 bottom bar */}
        </div>{/* end mt-8 space-y-4 sections wrapper */}
      </div>
    </div>
  );
}
